import { USER_FACING_ERROR_MESSAGE } from './constants';
import { SupersetDashboardIdResponse, SupersetGuestTokenResponse } from './types';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api-development.mybasepay.com';

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new Error(USER_FACING_ERROR_MESSAGE);
  }
}

export class SupersetForbiddenError extends Error {
  constructor() {
    super('Superset access forbidden');
    this.name = 'SupersetForbiddenError';
  }
}

function extractStringValue(
  data: string | Record<string, unknown>,
  key: string,
): string | null {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  const value = (data as Record<string, unknown>)[key];

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return null;
}

/** Fetches the dashboard id for the configured Superset dashboard. */
export async function fetchSupersetDashboardId(): Promise<string> {
  const url = `${API_BASE_URL}/account-service/apache-superset/dashboard-id`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 403) {
    throw new SupersetForbiddenError();
  }

  if (!response.ok) {
    throw new Error(USER_FACING_ERROR_MESSAGE);
  }

  const data = await parseJsonOrThrow<SupersetDashboardIdResponse>(response);
  const dashboardId =
    typeof data === 'string'
      ? extractStringValue(data, 'dashboardId')
      : extractStringValue(data, 'dashboardId');

  if (!dashboardId) {
    throw new Error(USER_FACING_ERROR_MESSAGE);
  }

  return dashboardId;
}

/** Returns a fresh guest token — used as the SDK fetchGuestToken callback. */
export async function fetchSupersetGuestToken(): Promise<string> {
  const url = `${API_BASE_URL}/account-service/apache-superset/guest-token`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 403) {
    throw new SupersetForbiddenError();
  }

  if (!response.ok) {
    throw new Error(USER_FACING_ERROR_MESSAGE);
  }

  const data = await parseJsonOrThrow<SupersetGuestTokenResponse>(response);
  const guestToken =
    typeof data === 'string'
      ? extractStringValue(data, 'guestToken')
      : extractStringValue(data, 'guestToken');

  if (!guestToken) {
    throw new Error(USER_FACING_ERROR_MESSAGE);
  }

  return guestToken;
}
